/**
 * Checks if a string is in base64 format, returning boolean
 *
 * @param {string} string String to test
 */
export const isBase64 = (string) => {
  const regex =
    /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/
  return regex.test(string)
}

import { LitElement, html, css, unsafeCSS } from '@hh/global.js'

class AdaptiveImage extends LitElement {
  constructor() {
    super()
    // This list of asset widths to select from will be comprehensive for most use cases
    this.srcsetWidths = [493, 600, 713, 823, 1100, 1206, 1346, 1426, 1646, 1946]

    // Check if we have any image data at all before proceeding
    if (!this.dataset.image && !this.dataset.mobileImage) {
      console.warn('AdaptiveImage: Component initialized without data-image or data-mobile-image attributes')
      this.style.display = 'none'
      return
    }

    const imageData = this.dataset.image && isBase64(this.dataset.image)
      ? atob(this.dataset.image)
      : this.dataset.image
    try {
      this.image = imageData ? JSON.parse(imageData) : null
    } catch (e) {
      this.image = imageData || null
    }

    const mobileImageData = this.dataset.mobileImage && isBase64(this.dataset.mobileImage)
      ? atob(this.dataset.mobileImage)
      : this.dataset.mobileImage
    try {
      this.mobileImage = mobileImageData ? JSON.parse(mobileImageData) : null
    } catch (e) {
      this.mobileImage = mobileImageData || null
    }

    this.width = this.image?.width || 1946
    this.mobileWidth = this.mobileImage?.width || 750
    this.src = this.image?.src || this.image
    this.mobileSrc = this.mobileImage?.src || this.mobileImage
    this.alt = this.dataset.alt || this.image?.alt || ''
    this.sizes = this.dataset.sizes || '100vw'
    this.loading = this.dataset.loading || 'lazy'
    this.objectFit = this.dataset.objectFit || 'cover'
    this.aspectRatio =
      this.image?.aspect_ratio || parseFloat(this.dataset.aspectRatio) || 1
    this.mobileAspectRatio =
      this.mobileImage?.aspect_ratio ||
      parseFloat(this.dataset.mobileAspectRatio) ||
      1
    this.zoomOnHover = this.dataset.zoomOnHover || false
    this.mobileBreakpoint = this.dataset?.mobileBreakpoint || false
    this.removeTransitionBehavior =
      this.hasAttribute('data-remove-transition-behavior') || false
    
    // Validate that we have a valid src before proceeding
    if (!this.src || this.src === 'null' || typeof this.src !== 'string') {
      console.warn('AdaptiveImage: Invalid or missing image src', {
        image: this.image,
        src: this.src,
        hasDataAttribute: !!this.dataset.image
      })
      this.style.display = 'none'
      return
    }
    
    this.style.setProperty('--hh-object-fit', this.objectFit)
    this.style.setProperty('--hh-aspect-ratio', this.aspectRatio)
    this.style.setProperty('--hh-mobile-aspect-ratio', this.mobileAspectRatio)

    this.attachShadow({ mode: 'open' })
    this.render()
  }

  connectedCallback() {
    super.connectedCallback()
  }

  updateImage(data) {
    Object.assign(this, data)
    this.render()
  }

  generateSrcset(src, imgWidth) {
    if (!src || typeof src !== 'string') return ''
    
    let srcset = ''
    const separator = src.includes('?') ? '&' : '?'

    for (const width of this.srcsetWidths) {
      if (imgWidth >= width) srcset += `${src}${separator}width=${width} ${width}w,`
    }
    srcset += `${src} ${imgWidth}w`
    return srcset
  }

  getImageSrc(src, width) {
    if (!src || typeof src !== 'string') return ''
    const separator = src.includes('?') ? '&' : '?'
    return `${src}${separator}width=${width}`
  }

  render() {
    const hasMobileImage = this.mobileImage && 
                          this.mobileSrc && 
                          typeof this.mobileSrc === 'string' && 
                          this.mobileSrc !== 'null'
    
    return html`
      <picture>
        ${hasMobileImage
          ? html`<source
                media="(min-width: ${this.mobileBreakpoint
                  ? this.mobileBreakpoint + 'px'
                  : '640px'})"
                srcset="${this.generateSrcset(this.src, this.width)}"
                sizes="${this.sizes}"
              />
              <source
                media="(max-width: ${this.mobileBreakpoint
                  ? this.mobileBreakpoint - 1 + 'px'
                  : '639px'})"
                srcset="
                  ${this.generateSrcset(this.mobileSrc, this.mobileWidth)}
                "
                sizes="${this.sizes}"
              />`
          : html`<source srcset="${this.generateSrcset(
              this.src,
              this.width
            )}" sizes="${this.sizes}"></source>`}

        <img
          class="image-element${this.zoomOnHover
            ? ' image-element--zoom-on-hover'
            : ''}
            ${this.removeTransitionBehavior
            ? ' image-element--remove-ease'
            : ''}"
          src="${this.getImageSrc(this.src, this.srcsetWidths[0])}"
          alt="${this.alt}"
          loading="${this.loading}"
        />
      </picture>
    `
  }

  static styles = css`
    :host {
      display: block;
      height: auto;
      position: relative;
      width: 100%;
      aspect-ratio: ${unsafeCSS(
        this.mobileAspectRatio !== 0
          ? 'var(--hh-mobile-aspect-ratio)'
          : 'var(--hh-aspect-ratio)'
      )};
    }
    @media screen and (min-width: 750px) {
      :host {
        aspect-ratio: var(--hh-aspect-ratio);
      }
    }
    @supports not (aspect-ratio: ${unsafeCSS('var(--hh-aspect-ratio)')}) {
      :host::before {
        float: left;
        padding-top: calc(100% / ${unsafeCSS('var(--hh-aspect-ratio)')});
        content: '';
      }
      :host::after {
        display: block;
        clear: both;
        content: '';
      }
    }
    .image-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: var(--hh-object-fit);
      transition: transform var(--hh-duration-medium) ease;
      -webkit-transform: translate3d(0, 0, 0);
    }
    .image-element--remove-ease {
      transition: transform var(--hh-duration-medium) !important;
    }
    :host(:hover) .image-element--zoom-on-hover {
      transform: scale(1.1);
    }
  `
}

if (!customElements.get('adaptive-image')) {
  customElements.define('adaptive-image', AdaptiveImage);
}